/* eslint-disable no-case-declarations */
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { fabric } from 'fabric'
import { BasePopoverConfermaOperazione, BaseFeedbackConIcona } from 'feedback'
import { ICONE } from 'icons'
import { BaseButtons, BaseCheckbox, BaseColorPicker, BaseSelectModal } from 'inputs'
import { BaseGridLayout } from 'layout'
import { BaseGrassetto, BaseTesto } from 'text'
import { MIME_TYPES, capitalizza } from 'utils'
import { useFabricJSEditor } from './fabricjs-react/editor'
import FabricJSCanvas from './fabricjs-react/FabricJSCanvas'
// import { jsonDaMobile, jsonDaWeb } from './fabricjs-react/JsonTest' // PER TESTARE JSON

export default function BaseEditorImmagine(props) {
  const {
    json,
    editorRef,
    modelli,
    getModelloConId,
    sfondoSrc,
    mimetypeImg = MIME_TYPES.PNG.type,
    abilitaColori = false,
    ...restProps
  } = props

  const { selectedObjects, editor, onReady } = useFabricJSEditor()

  /********** Logica azioni **********/

  function aggiungiElemento(modello) {
    switch (modello.id) {
      case 'cerchio':
        const circle = editor.addCircle()
        editor.canvas.setActiveObject(circle)
        break
      case 'rettangolo':
        const rectangle = editor.addRectangle()
        editor.canvas.setActiveObject(rectangle)
        break
      case 'testo':
        const placeholder = '(nuovo testo)'
        const text = editor.addText(placeholder)
        editor.canvas.setActiveObject(text)
        // text.enterEditing()
        // text.setSelectionStart(0)
        // text.setSelectionEnd(placeholder.length)
        break
      default:
        const { img, larghezza, altezza, tipo } = modello
        if (abilitaColori && tipo === 'svg') {
          fabric.loadSVGFromURL(img, (objects, options) => {
            const shape = fabric.util.groupSVGElements(objects, options)
            shape.set({
              left: 50 + larghezza / 2,
              top: 50 + altezza / 2,
              originX: 'center',
              originY: 'center',
              tipo,
            })
            editor.canvas.add(shape)
            editor.canvas.setActiveObject(shape)
          })
        } else {
          fabric.Image.fromURL(img, immagine => {
            immagine.set({
              left: 50 + larghezza / 2,
              top: 50 + altezza / 2,
              width: larghezza,
              height: altezza,
              originX: 'center',
              originY: 'center'
            })
            immagine.scaleToHeight(altezza)
            immagine.scaleToWidth(larghezza)
            editor.canvas.add(immagine)
            editor.canvas.setActiveObject(immagine)
          })
        }
        break
    }
  }

  const [modalitaDisegnoLibero, setModalitaDisegnoLibero] = useState(false)

  function toggleModalitaDisegnoLibero() {
    editor.canvas.isDrawingMode = !editor.canvas.isDrawingMode
    setModalitaDisegnoLibero(modalita => !modalita)
  }

  function cancellaElemento() {
    editor.deleteSelected()
  }

  function cancellaTutto() {
    if (sfondoSrc) {
      editor.deleteAll(({ __isSfondo }) => !__isSfondo)
    } else {
      editor.deleteAll()
    }
  }

  function bloccaElemento() {
    selectedObjects.forEach(elemento => {
      elemento.lockMovementX = true
      elemento.lockMovementY = true
      elemento.lockScalingX = true
      elemento.lockScalingY = true
      elemento.lockRotation = true
      elemento.hasControls = false
    })
    editor.canvas.renderAll()
  }

  function sbloccaElemento() {
    selectedObjects.forEach(elemento => {
      elemento.lockMovementX = false
      elemento.lockMovementY = false
      elemento.lockScalingX = false
      elemento.lockScalingY = false
      elemento.lockRotation = false
      elemento.hasControls = true
    })
    editor.canvas.renderAll()
  }

  function portaAvantiElemento() {
    selectedObjects.forEach(elemento => {
      editor.canvas.bringToFront(elemento)
    })
    editor.canvas.renderAll()
  }

  function portaIndietroElemento() {
    selectedObjects.forEach(elemento => {
      editor.canvas.sendToBack(elemento)
    })
    if (sfondoSrc) {
      editor.canvas.sendToBack(getElementoSfondo())
    }
    editor.canvas.renderAll()
  }

  function coloraElemento(colore) {
    selectedObjects.forEach(elemento => {
      if (elemento.type === 'text') {
        elemento.set({ fill: colore })
      } else if (elemento.fromSVG) {
        elemento.set({ fill: colore }) // Funziona solo se la svg ha un singolo elemento
      } else {
        elemento.set({ stroke: colore })
      }
    })
    editor.canvas.renderAll()
  }

  /***********************************/

  /******* I/O json e immagine *******/

  const [erroreLetturaJson, setErroreLetturaJson] = useState(false)

  function getImage() {
    const mappaturaFormati = {
      [MIME_TYPES.PNG.type]: 'png',
      [MIME_TYPES.JPG.type]: 'jpeg',
      'image/jpg': 'jpeg',
    }
    return editor.canvas.toDataURL({
      format: mappaturaFormati[mimetypeImg],
      quality: 1
    })
  }

  function getJSON() {
    if (erroreLetturaJson) return { ok: false }
    return { ok: true, json: editor.canvas }
  }

  useImperativeHandle(editorRef, () => ({ getJSON, getImage }))

  const getElementoSfondo = useCallback(() => {
    return editor?.canvas.getObjects().find(({ __isSfondo }) => __isSfondo)
  }, [editor?.canvas])

  const sfondoCaricatoRef = useRef(false)

  useEffect(() => {
    if (sfondoSrc && editor && !sfondoCaricatoRef.current) {
      const sfondo = getElementoSfondo()
      if (!sfondo) {
        sfondoCaricatoRef.current = true
        fabric.Image.fromURL(sfondoSrc, immagine => {
          immagine.scaleToHeight(editor.canvas.height)
          // immagine.scaleToWidth(editor.canvas.width)
          immagine.lockMovementX = true
          immagine.lockMovementY = true
          immagine.lockScalingX = true
          immagine.lockScalingY = true
          immagine.lockRotation = true
          immagine.hasControls = false
          immagine.selectable = false
          immagine.__isSfondo = true
          editor.canvas.add(immagine)
        })
      }
    }
  }, [sfondoSrc, editor, getElementoSfondo])

  function transformJson(json) {
    if (json) {
      try {
        const jsonParsato = JSON.parse(json)
        jsonParsato.objects.forEach(object => {
          if (!object.src) return
          const segmentiSrc = object.src.split('/')
          const idModelloConEstensione = segmentiSrc[segmentiSrc.length - 1]
          const idModello = idModelloConEstensione.split('.')[0]
          object.src = getModelloConId(idModello).img
        })
        return jsonParsato
      } catch (error) {
        setErroreLetturaJson(true)
      }
    }
    return json
  }

  if (erroreLetturaJson) {
    return (
      <BaseFeedbackConIcona.Errore
        messaggio="Errore: l'immagine non può essere modificata perché non risulta salvata nel formato corretto."
      />
    )
  }

  /***********************************/

  const propsComuniBottoni = {
    variant: 'outlined',
    color: 'default',
    ...(!selectedObjects.length > 0 && { disabled: true })
  }

  let disposizioneExtra = []
  if (abilitaColori) disposizioneExtra.push(1)

  return (
    <div>
      <FabricJSCanvas
        onReady={onReady}
        initialJson={json}
        transformInitialJson={transformJson}
        backgroundColor='white'
        {...restProps}
      />

      <BaseGridLayout disposizioneRighe={[2, 2, 2, 2, ...disposizioneExtra]} alignCenter>
        <BaseSelectModal
          label='Aggiungi elemento'
          propsBottoneModifica={{
            variant: 'outlined',
            color: 'success',
            iconaInizio: ICONE.AGGIUNGI,
            noMarginY: false
          }}
          onChange={e => aggiungiElemento(e.target.value)}
          nonUsareHookForm

          options={modelli}
          renderOption={({ id, nome }) =>
            <BaseGridLayout alignCenter>
              <img
                src={getModelloConId(id).img}
                style={{ width: '6rem', maxHeight: '6rem', padding: 8 }}
              />
              <BaseGrassetto>{capitalizza(nome)}</BaseGrassetto>
            </BaseGridLayout>
          }
          getOptionKey={({ id }) => id}
          getOptionLabel={({ nome }) => nome}
          getOptionSearchString={({ nome }) => nome}
          labelRicerca='nome'
          {...restProps}
        />

        <BaseCheckbox
          switch
          label='Modalità disegno libero'
          nonUsareHookForm
          value={modalitaDisegnoLibero}
          onChange={toggleModalitaDisegnoLibero}
        />

        <BasePopoverConfermaOperazione
          messaggio='Confermare la cancellazione?'
          onConferma={cancellaElemento}
        >
          <BaseButtons.Elimina
            testo='Cancella elemento'
            {...propsComuniBottoni}
            color='secondary'
          />
        </BasePopoverConfermaOperazione>
        <BasePopoverConfermaOperazione
          messaggio='Confermare la cancellazione?'
          onConferma={cancellaTutto}
        >
          <BaseButtons.Elimina
            testo='Cancella tutto'
            variant='outlined'
          />
        </BasePopoverConfermaOperazione>

        <BaseButtons.Custom
          testo='Blocca elemento'
          iconaInizio={ICONE.LUCCHETTO_CHIUSO}
          onClick={bloccaElemento}
          {...propsComuniBottoni}
        />
        <BaseButtons.Custom
          testo='Sblocca elemento'
          iconaInizio={ICONE.LUCCHETTO_APERTO}
          onClick={sbloccaElemento}
          {...propsComuniBottoni}
        />

        <BaseButtons.Custom
          testo='Porta avanti elemento'
          iconaInizio={ICONE.FRECCIA_A_DESTRA}
          onClick={portaAvantiElemento}
          {...propsComuniBottoni}
        />
        <BaseButtons.Custom
          testo='Porta indietro elemento'
          iconaInizio={ICONE.FRECCIA_INDIETRO}
          onClick={portaIndietroElemento}
          {...propsComuniBottoni}
        />

        <BaseColorPicker
          onColoreScelto={colore => coloraElemento(colore)}
          propsBottone={{ ...propsComuniBottoni, testo: 'Cambia colore elemento', color: 'info' }}
        />
      </BaseGridLayout>

      <BaseTesto color='textSecondary' piccolo sx={{ mt: 1 }}>
        Selezionare un elemento dell'immagine per abilitare tutte le azioni.
      </BaseTesto>

      {/* PER TESTARE JSON
        <button onClick={() => console.log(getJSON())}>Get json</button>
        <button onClick={() => setJSON(jsonDaWeb)}>Set json da web</button>
        <button onClick={() => setJSON(jsonDaMobile)}>Set json da mobile</button> 
      */}
    </div>
  )
}